import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { CreditCard, Landmark, Loader2 } from 'lucide-react';
import { formatDate, formatMoney } from './constants';

export default function ModalsSepaStripe({
  getAmount, getName, handleSetupSEPA, handleStripePayment, processing, selectedDoc,
  selectedEcheanceIndex, sepaForm, setSepaForm, setShowSEPAModal, setShowStripeModal,
  setStripePaymentType, showSEPAModal, showStripeModal, stripePaymentType,
}) {
  return (
    <>
      {/* Modal Paiement Stripe CB */}
      <Dialog open={showStripeModal} onOpenChange={setShowStripeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Paiement Stripe
            </DialogTitle>
            <DialogDescription>
              Paiement sécurisé par carte bancaire ou SEPA
            </DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-500">Référence</span>
                  <span className="font-mono text-sm">{selectedDoc.reference}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-500">Souscripteur</span>
                  <span className="font-medium">{getName(selectedDoc)}</span>
                </div>
                <Separator className="my-3" />
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Montant à payer</span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {selectedEcheanceIndex !== null
                      ? formatMoney(selectedDoc.echeancier_personnalise?.echeances?.[selectedEcheanceIndex]?.montant || 0)
                      : formatMoney(getAmount(selectedDoc))
                    }
                  </span>
                </div>
                {selectedEcheanceIndex !== null && (
                  <Badge className="mt-2 bg-blue-700 text-white font-semibold">
                    Échéance {selectedEcheanceIndex + 1}
                  </Badge>
                )}
              </div>

              <div className="space-y-3">
                <Label>Méthode de paiement</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={stripePaymentType === 'card' ? 'default' : 'outline'}
                    className={stripePaymentType === 'card' ? 'bg-indigo-600' : ''}
                    onClick={() => setStripePaymentType('card')}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Carte bancaire
                  </Button>
                  <Button
                    type="button"
                    variant={stripePaymentType === 'sepa_debit' ? 'default' : 'outline'}
                    className={stripePaymentType === 'sepa_debit' ? 'bg-blue-600' : ''}
                    onClick={() => setStripePaymentType('sepa_debit')}
                  >
                    <Landmark className="h-4 w-4 mr-2" />
                    SEPA Direct
                  </Button>
                </div>
              </div>

              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Vous serez redirigé vers la page de paiement sécurisé Stripe.
                  Une fois le paiement effectué, vous serez automatiquement redirigé.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowStripeModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleStripePayment}
              disabled={processing}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {processing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirection...</>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Procéder au paiement
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Configuration SEPA Récurrent */}
      <Dialog open={showSEPAModal} onOpenChange={setShowSEPAModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-blue-600" />
              Configuration Prélèvement SEPA
            </DialogTitle>
            <DialogDescription>
              Configurez un prélèvement automatique pour les échéances
            </DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-4">
              {/* Type de SEPA */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Type de prélèvement SEPA</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      sepaForm.sepaType === 'core'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSepaForm(prev => ({ ...prev, sepaType: 'core' }))}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        sepaForm.sepaType === 'core' ? 'border-blue-500' : 'border-gray-300'
                      }`}>
                        {sepaForm.sepaType === 'core' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      </div>
                      <span className="font-semibold text-blue-700">SEPA Core</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">Pour les <strong>particuliers</strong></p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• Droit de remboursement 8 semaines</li>
                      <li>• Délai de prélèvement: 2-5 jours</li>
                      <li>• Mandat simplifié</li>
                    </ul>
                  </div>

                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      sepaForm.sepaType === 'b2b'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSepaForm(prev => ({ ...prev, sepaType: 'b2b' }))}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        sepaForm.sepaType === 'b2b' ? 'border-indigo-500' : 'border-gray-300'
                      }`}>
                        {sepaForm.sepaType === 'b2b' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                      </div>
                      <span className="font-semibold text-indigo-700">SEPA B2B</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">Pour les <strong>entreprises</strong></p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      <li>• Pas de droit de remboursement</li>
                      <li>• Délai de prélèvement: 1 jour</li>
                      <li>• Confirmation mandat requise</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-blue-700">Référence</span>
                  <span className="font-mono text-sm">{selectedDoc.reference}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-blue-700">Montant total</span>
                  <span className="font-bold text-blue-800">{formatMoney(getAmount(selectedDoc))}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-700">Nombre d'échéances</span>
                  <span className="font-medium">{selectedDoc.echeancier_personnalise?.echeances?.length || 'Non défini'}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Nom du titulaire du compte {sepaForm.sepaType === 'b2b' && <span className="text-indigo-600">(Raison sociale)</span>}</Label>
                  <Input
                    value={sepaForm.customerName}
                    onChange={(e) => setSepaForm(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder={sepaForm.sepaType === 'b2b' ? "Raison sociale de l'entreprise" : "Nom complet"}
                  />
                </div>
                <div>
                  <Label>Email {sepaForm.sepaType === 'b2b' && <span className="text-indigo-600">(comptabilité)</span>}</Label>
                  <Input
                    type="email"
                    value={sepaForm.customerEmail}
                    onChange={(e) => setSepaForm(prev => ({ ...prev, customerEmail: e.target.value }))}
                    placeholder="email@exemple.com"
                  />
                </div>
              </div>

              {/* Aperçu des échéances */}
              {selectedDoc.echeancier_personnalise?.echeances?.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg border max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-gray-600 mb-2">Échéances programmées</p>
                  {selectedDoc.echeancier_personnalise.echeances.map((ech, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs py-1 border-b border-gray-100 last:border-0">
                      <span className="text-gray-500">Éch. {idx + 1} - {formatDate(ech.date)}</span>
                      <span className="font-mono font-medium">{formatMoney(ech.montant)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Info spécifique au type */}
              {sepaForm.sepaType === 'core' ? (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs font-semibold text-blue-700 mb-1">📋 SEPA Core - Particuliers</p>
                  <p className="text-xs text-blue-600">
                    Le débiteur dispose d'un droit de remboursement de 8 semaines après le prélèvement.
                    Notification envoyée 14 jours avant chaque prélèvement.
                  </p>
                </div>
              ) : (
                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                  <p className="text-xs font-semibold text-indigo-700 mb-1">🏢 SEPA B2B - Entreprises</p>
                  <p className="text-xs text-indigo-600">
                    <strong>Attention:</strong> Pas de droit de remboursement après prélèvement.
                    La banque du débiteur doit confirmer le mandat avant le premier prélèvement.
                    Délai de traitement réduit à 1 jour ouvré.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSEPAModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSetupSEPA}
              disabled={processing || !sepaForm.customerName || !sepaForm.customerEmail}
              className={sepaForm.sepaType === 'b2b'
                ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              }
            >
              {processing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Configuration...</>
              ) : (
                <>
                  <Landmark className="h-4 w-4 mr-2" />
                  Configurer SEPA {sepaForm.sepaType === 'b2b' ? 'B2B' : 'Core'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
